/**
 * What: Shopify app merchant-facing copy.
 * Transport / data-flow: Pure string dictionary segment merged by shopify-component-copy.ts for Shopify UI consumers.
 *
 */
export const shopifyComponentAppCopy = {
    productDetails: {
        statusActive: 'Active',
        statusDraft: 'Draft',
        statusArchived: 'Archived',
    },
    settingsPage: {
        storeIntelligenceIsAnalyzedAnd001: 'Shopper Intelligence is analyzed when you design a new product test setup. Available store-level patterns can provide context for that setup.',
        testfeedIsDesignedWithPrivacy002: 'TestFeed uses aggregated store-level patterns as optional context for simulated shopper profiles. Results are directional. They compare versions inside this test only.',
        privacySafeguards003: 'Privacy safeguards',
        zeroCustomerPiiNoNames004: 'Shopper Intelligence never asks for customer names, emails, addresses, or phone numbers.',
        statisticalGroundingCustomerSegmentsAre005: "Eligible store patterns are summarized before they're used as test context.",
        needHelpConfiguringYourTestfeed006: 'Need help configuring your TestFeed integration or want to learn more about how simulated shoppers work?',
        checkOutOurDocumentationTo007: 'Read our documentation to understand how TestFeed produces directional simulated shopper feedback for early product decisions.',
        documentation008: 'Documentation',
        contactSupport009: 'Contact Support',
        bookStrategyCall010: 'Book a strategy call',
    },
    shopifyAppNavigation: {
        home001: 'Home',
        studies002: 'Tests',
        microSurveys003: 'Micro-surveys',
        pixelEvents004: 'Pixel events',
        integrations006: 'Integrations',
        settings007: 'Settings',
        insights008: 'Insights',
    },
    shopifyErrorBoundary: {
        appError001: 'App Error',
        somethingWentWrong002: 'Something went wrong',
        theTestfeedAppEncounteredA003: 'The TestFeed app encountered a critical error. Please reload the page or contact support if the issue persists.',
    },
    shopifySupportPage: {
        intro001: 'Tell us what happened and we will review it with your store and run context attached.',
        followUp002: 'Our team will follow up with you at the email address you provide below.',
        runDetails003: 'Run details',
        setupId004: 'Setup',
        packetId005: 'Test',
        runId006: 'Run',
        latestStepKey007: 'Latest step',
        noRunContext008: 'No failed run context was provided. You can still send a general support request.',
        sendSupportRequest009: 'Send support request',
        sendingSupportRequest010: 'Sending support request',
        supportRequestSent011: 'Support request sent',
        unableToSendRequest012: 'Unable to send support request',
        requestReference013: 'Request reference:',
        describeProblemLabel014: "What's going on?",
        describeProblemPlaceholder015: 'Tell us what happened, what you expected, and what you saw instead.',
        emailLabel016: 'Your email',
        emailPlaceholder017: 'you@example.com',
        describeProblemRequired018: 'Please describe what happened',
        emailRequired019: 'Please enter your email',
        emailInvalid020: 'Please enter a valid email address',
        helpModalHeading: 'What gets sent with a support request',
        helpModalBody1: 'Your message is sent along with the run details shown above, if any, so our team has the context to help without you re-explaining what happened.',
        helpModalBody2: 'Nothing is sent until you press "Send support request" below.',
        runDetailsInfoWhy: "Knowing what's attached helps you decide how much detail to add in your message below.",
        runDetailsInfoHow: 'These are read from the test that brought you here — not re-entered or guessed.',
    },
    shopifyHomePage: {
        heroEyebrow: 'WELCOME TO TESTFEED',
        heroTitle: 'Find out what sells better — without touching your store.',
        heroSubtitle: 'Pick a product. Try a different price, description, or image. TestFeed shows it to 200 shoppers and tells you which version wins — before you change anything real.',
        reassurance1Lead: "The shoppers aren't real people — and that's the point.",
        reassurance1Body: "They're simulations built from your own customers and order history, so you get an answer in minutes, not weeks, and nobody sees a test by accident.",
        reassurance2Lead: 'You get a decision, not a dashboard.',
        reassurance2Body: 'Every test ends in one sentence — like "Drop the price to $18" — with the reasons behind it in plain English.',
        reassurance3Lead: 'Your store never changes unless you say so.',
        reassurance3Body: 'TestFeed never touches your prices, products, or pages on its own — you decide when a winning version goes live.',
        heroImageAlt: "A grocer weighing up two versions of the same cereal box, labelled A and B — version B wearing the winner's ribbon.",
        heroImageCaption: "Which one wins? That's the whole job.",
        reassurance4Lead: 'Your data stays private.',
        reassurance4Body: 'Only aggregated, anonymised patterns are used as context — never customer names, emails, or contact details.',
        workedExampleEyebrow: "WHAT YOU'LL ACTUALLY GET",
        workedExampleTitle: 'One verdict, not a dashboard.',
        workedExampleQuote: '“Drop the price to $18. Shoppers reacted to the number, not the packaging.”',
        workedExampleImageAlt: 'A shopper icon representing simulated shopper feedback.',
        howItWorksToggle: 'See how a test works',
        howItWorksTitle: 'How a test works',
        howItWorksStep1: 'Pick a product from your store.',
        howItWorksStep2: 'Write the two versions to compare — say, your price against a lower one.',
        howItWorksStep3: "It's shown to 200 simulated shoppers, who react.",
        howItWorksStep4: 'Read which one won, and why. Then decide.',
    },
    getStartedCard: {
        title: 'Get started',
        setupStepsAriaLabel: 'Setup steps',
        intro: 'Complete these steps to run your first test.',
        progressLabel: '{completed} out of {total} steps completed',
        toggleGuideAction: 'Toggle the get started guide',
        completeMarker: '✓',
        completeAction: 'Done',
        waitingAction: 'Waiting',
        stepDoneStatus: 'Complete',
        stepNotStartedStatus: 'Not started',
        stepErrorStatus: 'Needs attention',
        stepLoadingStatus: 'In progress',
        connectStepTitle: 'Connect to your TestFeed account',
        connectStepDescription: 'Create or sign in to your TestFeed account. This store links as you connect.',
        connectStepAction: 'Open settings',
        connectDirectAction: 'Connect TestFeed',
        planStepTitle: 'Choose your plan',
        planStepDescription: 'Choose the credit allowance that fits your store.',
        planStepAction: 'Choose a plan',
        planPendingStatus: 'Plan selected — confirming with Shopify',
        planStalledStatus: "We couldn't confirm your plan yet",
        checkPlanStatusAction: 'Check status',
        planConfirmingStatus: 'Confirming…',
        viewPlansAction: 'View plans',
        planSelectedAction: '{planName} plan selected',
        internalPlanSelectedAction: 'TestFeed plan selected',
        syncStepTitle: 'Sync your store data',
        syncStepDescription: "TestFeed reads your orders and customers, then shows you what's worth testing first.",
        syncAction: 'Sync store data',
        resyncAction: 'Resync store data',
        connectStepImageAlt: 'An inspector checking a connection.',
        planStepImageAlt: 'A shopper weighing up two versions of the same bottle.',
        syncStepImageAlt: 'A scientist reading notes on a clipboard.',
        syncingStatus: 'Syncing…',
        syncErrorFallback: "We couldn't finish syncing your store.",
        syncRetryAction: 'Check status',
        syncDoneBody: 'Your shopper intelligence is ready.',
        syncDoneAction: "See what's worth testing",
        allDoneTitle: "You're set up",
        allDoneBody: 'Your store is connected and your shopper intelligence is ready. Pick a product and run your first test.',
        allDoneBodyWithOpportunity: 'Your store is connected and your shopper intelligence is ready. {observation}',
    },
    houseImagery: {
        supportContactName: 'Millie',
        supportContactAlt: 'Millie, who answers TestFeed support requests.',
    },
    pageNotFound: {
        contactSupport: 'Get in touch',
        backToTests: 'Back to Tests',
    },
    studyRunPage: {
        pageTitle: 'Review and run test',
        generalAudienceLabel: 'General shoppers',
        readyStatus: 'Ready to run',
        checkingStatus: 'Checking',
        notReadyStatus: 'Not ready',
        runningStatus: 'Test running',
        reviewHeading: 'Review your setup',
        productLabel: 'Product',
        audienceLabel: 'Audience',
        savedSetupBody: 'Your setup is saved. Check its readiness below before starting the test.',
        dryRunBody: 'We check the setup again before the test starts.',
        runDetailsHeading: 'Before you run',
        leavePageBody: "You can leave this page and come back to check progress. We'll notify you when results are ready.",
        capacityUseBody: 'This run uses {credits} credits.',
        capacityBalanceBody: '{remaining} credits available now · {remainingAfter} credits remaining after this run.',
        missingPlanBody: 'Choose a TestFeed plan before you run this test.',
        runStudyAction: 'Run test',
        backToStudiesAction: 'Back to tests',
        checkingReadiness: 'Checking test readiness…',
        retryReadinessAction: 'Try again',
        studyStartedBody: "Your test has started. You can leave this page; we'll notify you when results are ready.",
        viewTestProgressAction: 'View test progress',
        missingSetupBody: "We couldn't find this saved setup. Return to Tests and choose a setup to review.",
        missingPacketBody: 'This setup is not ready to run yet. Return to the test builder to review it.',
        runErrorBody: "We couldn't start this test. Please try again.",
        helpModalHeading: 'How running a test works',
        helpModalBody1: "We check your setup is still ready right before the test starts. Once it's running, you can close this page — nothing stops, and we'll let you know when results are in.",
        helpModalBody2: "Results land on the test's page as soon as they're ready.",
        creditInfoHeading: 'About this run’s credits',
        creditInfoWhy: 'Knowing the cost before you run helps you decide if now’s the right time, or if you should wait.',
        creditInfoHow: 'The credit count is quoted fresh for this exact setup. Your remaining balance updates once the run starts.',
    },
    connectionRecovery: {
        shopifyAccess: 'Reconnect Shopify access',
        reviewConnection: 'Review TestFeed connection',
        openSettings: 'Open Settings',
    },
    shopifyApiErrors: {
        shopifyAccessNeedsReconnect: 'Reconnect Shopify access to continue.',
        shopifyBusy: 'Shopify is busy. Please wait a moment and try again.',
        connectionDisconnected001: 'Your store is no longer connected to TestFeed. Reconnect to continue.',
        noEligibleUsagePlan002: "This isn't available on your current plan.",
        somethingWentWrong003: 'Something went wrong. Please try again.',
        couldNotLoad004: "We couldn't load this. Please try again.",
        couldNotSave005: "We couldn't save your changes. Please try again.",
        couldNotStartRun006: "We couldn't start this test. Please try again.",
        couldNotPrepareRun007: "We couldn't prepare this test. Please try again.",
        couldNotSaveAudienceSet008: "We couldn't save this audience. Please try again.",
        couldNotSaveMicroSurvey009: "We couldn't save this micro-survey. Please try again.",
        couldNotArchiveMicroSurvey010: "We couldn't archive this micro-survey. Please try again.",
        couldNotSendSupportRequest011: "We couldn't send your request. Please try again.",
        couldNotUploadImage012: "We couldn't upload this image. Please try again.",
        couldNotDiscardDraft014: "We couldn't discard this draft. Please try again.",
        couldNotConnectTestFeed015: "We couldn't connect to TestFeed. Please try again.",
        couldNotDisconnectTestFeed016: "We couldn't disconnect TestFeed. Please try again.",
        couldNotDeleteStudySetup017: "We couldn't delete this test setup. Please try again.",
        couldNotSyncStoreData018: "We couldn't update your store data. Your last available data is still shown.",
        connectionNotFound019: "We couldn't find your TestFeed connection. Reconnect your store to continue.",
        connectionUninstalled020: 'TestFeed was uninstalled from this store. Reinstall it to continue.',
        connectionSelectionRequired021: 'Choose which TestFeed workspace this store belongs to before continuing.',
        connectionStatusUnavailable022: "We couldn't check your TestFeed connection. Please try again.",
        comparisonProjectionFailed023: "We couldn't apply your comparison to the survey questions. Review the survey and try again.",
        defaultProjectNotConfigured024: 'Choose a TestFeed project before preparing tests.',
        studyNotReadyToRun025: "This test isn't ready to run yet. Resolve the items in review and try again.",
        questionCheckUnavailable026: "We couldn't check your questions right now. Please try again.",
        productSelectionLocked027: 'This test is tied to this product. Discard the draft to start with a different product.',
        creditsExhausted028: "You've used all of this month's credits on your current plan.",
        staleEvidence029: 'Product data has changed. Reload the product to continue.',
        modelMissing030: "AI model isn't configured. Please contact support.",
        connectionUnavailable031: "We couldn't connect to TestFeed. Please try again.",
        comparisonNoOp032: 'One of your alternatives is the same as your current product, so there is nothing to compare. Change or remove it and try again.',
        reviewAgain033: 'This test was reviewed before a recent update. Review it again before running it.',
        studyRunAccepted037: 'This test has started and can no longer be edited. Create a new draft to make changes.',
        researchInputUnavailable034: 'This field is no longer on the product in Shopify. Refresh the product and choose another field.',
        researchInputIneligible035: "This field can't be tested. Choose another field.",
        researchInputTypeUnsupported036: "This field can't be tested this way. Choose another type of test.",
        revisionConflict038: 'Someone saved this test somewhere else (another tab or person) after you opened it. Load the saved version to continue — your unsaved changes here will be replaced.',
        validationBlocked039: 'Finish the test setup before submitting. Resolve the items in review and try again.',
        storeSelectionChanged040: 'The selected store changed. Reload before answering.',
    },
};
//# sourceMappingURL=app.js.map